import { Component, signal, ViewChild, OnInit, AfterViewInit, CUSTOM_ELEMENTS_SCHEMA, Inject, computed } from '@angular/core';
import { UserGeography } from '../../../shared/models/user-geography.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TreeModule } from '@odymaui/angular-tree-component';
import { ITreeOptions, TreeComponent } from '@circlon/angular-tree-component';
import { IGeographyNode } from '../../../shared/interfaces/geography-node.interface';
import { SearchBarComponent } from '../../search-bar/search-bar';
import { Geography } from '../../../shared/models/geography.model';
import { TreeNodeService } from '../../../core/services/tree-node.service';
import { GeographyService } from '../../../core/services/geography.service';
import { ISelectedGeography } from '../../../shared/interfaces/selected-geography.interface';
import type { DialogData } from '../../../shared/interfaces/dialog-data.interface';
import { GeographyMenuOption } from '../../../shared/models/geography-menu-option.model';
import { ClaimsService } from '../../../core/services/claims.service';
import { Subject, take, takeUntil } from 'rxjs';
import { Coverage } from '../../../shared/models/coverage.model';
import { ContextMenuComponent } from '../../../shared/components/context-menu-geography/context-menu-geography';
import { FavouriteGeography } from '../../../shared/models/favourite-geography.response.model';
import { IFavouriteGeographyNode } from '../../../shared/models/favourite-geography-node.model';
@Component({
  selector: 'emma-geography-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    SearchBarComponent,
    TreeModule,
    ContextMenuComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true,
  templateUrl: './geography-dialog.html',
  styleUrls: ['./geography-dialog.scss'],
})

export class GeographyDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('tree') public tree!: TreeComponent;
  @ViewChild(SearchBarComponent) public searchBar!: SearchBarComponent;

  public geographiesWithMissingParents = signal<Array<Geography>>([]);
  public selectedGeographies = signal<Array<ISelectedGeography>>([]);
  public userGeographies = signal<ReadonlyArray<UserGeography>>([]);
  public fetchedGeographyTypeIds = signal<Set<number>>(new Set());
  public coverage = signal<Array<Coverage>>([]);

  public isDataLoading = signal<boolean>(true);
  public nodes = signal<Array<IGeographyNode>>([]);
  public treeNodes = computed(() => this.nodes());
  public selectedItems = signal<Array<ISelectedGeography>>([]);

  public options: ITreeOptions = {
    idField: 'emiCode',
    useTriState: false
  };

  public favouriteGeographies = signal<Array<FavouriteGeography>>([]);
  public favouriteNodes = signal<Array<IFavouriteGeographyNode>>([]);
  public isFavouritesSectionExpanded = signal<boolean>(true);
  public combinedSearchNodes = signal<Array<IGeographyNode>>([]);

  private readonly highlightedFavouriteCodes = new Set<string>();

  constructor(
    public dialog: MatDialogRef<GeographyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
    private readonly treeNodeService: TreeNodeService,
    private readonly geographyService: GeographyService,
    private readonly claimService: ClaimsService,
  ) { }

  public ngOnInit(): void {
    this.geographiesWithMissingParents.set(this.dialogData.projectGeographies ?? []);
    this.userGeographies.set(this.dialogData.geographies ?? []);
    this.selectedGeographies.set(this.dialogData.selectedGeographies ?? []);
    this.favouriteGeographies.set(this.dialogData.favouriteGeographies ?? []);
    this.buildGeographiesTree();
    this.restoreHighlightedFavourites(); 
  }

  public ngAfterViewInit(): void {
    if (this.searchBar) {
      setTimeout(() => {
        this.searchBar.focusSearchInput();
      }, 100);
    }
  }

  public onInitialized(): void {
    if (this.tree.treeModel.getFirstRoot()) {
      this.tree.treeModel.getFirstRoot().expand();
    }
    this.refreshNodesMenuOptions();
  }

  public onSelectedNodeChange(selectedNode?: IGeographyNode): void {
    if (selectedNode) {
      const updatedNodes = this.toggleNodeSelection(this.nodes(), selectedNode.code);
      this.nodes.set(updatedNodes);
    }
    const updated = this.treeNodeService.flattenGeographySelections(this.nodes());
    this.selectedItems.set(updated);
    this.refreshNodesMenuOptions();
    this.refreshFavouriteNodes();
  }

  public onSearchSelection(selectedOption: IGeographyNode | IFavouriteGeographyNode): void {
    const asFavourite = selectedOption as IFavouriteGeographyNode;
    if (asFavourite?.isFavourite) {
        const realFav = this.favouriteNodes().find(f => f.code === asFavourite.code);
        if (realFav) {
            this.highlightedFavouriteCodes.add(realFav.code);
            const updatedNodes = this.treeNodeService.setSelectionByCode(
                this.nodes(),
                realFav.children.map((n: IGeographyNode) => n.code),
                true
            );
            this.nodes.set(updatedNodes);
        }
    } else {
        const updatedNodes = this.toggleNodeSelection(
            this.nodes(),
            (selectedOption as IGeographyNode).code
        );
        this.nodes.set(updatedNodes);
    }
    const updated = this.treeNodeService.flattenGeographySelections(this.nodes());
    this.selectedItems.set(updated);
    this.refreshNodesMenuOptions();
    this.refreshFavouriteNodes();
  }

  public onFavouriteToggle(favourite: IFavouriteGeographyNode): void {
      if (favourite.isSelected) {
          this.highlightedFavouriteCodes.add(favourite.code);
          const updatedNodes = this.treeNodeService.setSelectionByCode(
              this.nodes(),
              favourite.children.map((n: IGeographyNode) => n.code),
              true
          );
          this.nodes.set(updatedNodes);
      } else {
          this.highlightedFavouriteCodes.delete(favourite.code);

          const otherHighlighted = this.favouriteNodes().filter(
              f => this.highlightedFavouriteCodes.has(f.code)
          );
          const protectedCodes = new Set<string>();
          otherHighlighted.forEach(f => {
              f.children.forEach((n: IGeographyNode) => {
                  protectedCodes.add(n.code);
                  this.collectDescendantCodes(n, protectedCodes);
              });
          });

          const codesToDeselect = favourite.children
              .map((n: IGeographyNode) => n.code)
              .filter((code: string) => !protectedCodes.has(code));

          const updatedNodes = this.treeNodeService.setSelectionByCode(
              this.nodes(),
              codesToDeselect,
              false
          );
          this.nodes.set(updatedNodes);
      }
      const updated = this.treeNodeService.flattenGeographySelections(this.nodes());
      this.selectedItems.set(updated);
      this.refreshNodesMenuOptions();
      this.refreshFavouriteNodes();
  }

  public toggleFavouriteExpand(favourite: IFavouriteGeographyNode, event: Event): void {
      event.stopPropagation();
      this.favouriteNodes.update(nodes =>
          nodes.map(f =>
              f.code === favourite.code
                  ? { ...f, isExpanded: !f.isExpanded }
                  : f
          )
      );
  }

  public toggleFavouriteSection(): void {
      this.isFavouritesSectionExpanded.update(v => !v);
  }

  private toggleNodeSelection(nodes: Array<IGeographyNode>, code: string): Array<IGeographyNode> {
    return nodes.map(node => {
      if (node.code === code) {
        return { ...node, isSelected: !node.isSelected };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: this.toggleNodeSelection(node.children, code) };
      }
      return node;
    });
  }

  public onConfirmSelection(): void {
    const selected = this.selectedItems();
    const selectedGeographiesTypeIds = [...new Set(selected.map(g => g.typeId))];

    if (this.selectedGeographyTypeIdsChanged(selectedGeographiesTypeIds)) {
      this.updateCoverage(selectedGeographiesTypeIds);
    }
    this.selectedGeographies.set(selected);
    this.dialog.close({
      selectedGeographies: this.selectedGeographies()
    });
  }

  public onClearAll(): void {
    const updatedNodes = this.treeNodeService.deselectAllGeographyTreeNodes(this.nodes());
    this.nodes.set(updatedNodes);
    this.selectedItems.set([]);
    this.highlightedFavouriteCodes.clear();
    this.favouriteNodes.update(nodes =>
      nodes.map(f => ({ ...f, isSelected: false, isExpanded: false }))
    );
    this.refreshCombinedSearchNodes();
    this.refreshNodesMenuOptions();
  }

  public onCancel(): void {
    this.dialog.close();
  }

  public onRemoveSelection(geographyCode: string): void {
    const updatedNodes = this.treeNodeService.deselectGeographyTreeNode(this.nodes(), geographyCode);
    this.nodes.set(updatedNodes);
    this.selectedItems.set(this.selectedItems().filter(item => item.emiCode !== geographyCode));
    this.favouriteNodes().forEach(fav => {
        if (
            this.highlightedFavouriteCodes.has(fav.code) &&
            fav.children.some((child: IGeographyNode) => child.code === geographyCode)
        ) {
            this.highlightedFavouriteCodes.delete(fav.code);
        }
    });
    this.refreshNodesMenuOptions();
    this.refreshFavouriteNodes();
    this.refreshCombinedSearchNodes();
  }

  public onGeographyMenuClick(menuItem: GeographyMenuOption, selectedNode: IGeographyNode): void {
    const updatedSelections = this.treeNodeService.handleGeographyMenuClick(
      menuItem,
      selectedNode,
      [...this.userGeographies()],
      [...this.nodes()],
      this.geographyService
    );
    this.selectedItems.set(updatedSelections);
    this.refreshNodesMenuOptions();
    this.refreshFavouriteNodes();
  }

  private selectedGeographyTypeIdsChanged(selectedGeographiesTypeIds: Array<number>): boolean {
    const existing = [...new Set(this.selectedItems().map(x => x.typeId))];
    const areEqual =
      existing.length === selectedGeographiesTypeIds.length &&
      existing.every(id => selectedGeographiesTypeIds.includes(id));

    return !areEqual;
  }

  private updateCoverage(selectedGeographiesTypeIds: Array<number>): void {
    const fetched = this.fetchedGeographyTypeIds();
    const geographyTypeIdsToLoad = selectedGeographiesTypeIds.filter(id => !fetched.has(id));

    if (geographyTypeIdsToLoad.length !== 0) {
      const projectCode = this.dialogData.projectCode;
      this.claimService.getProjectCoverage(projectCode, geographyTypeIdsToLoad)
        .pipe(take(1))
        .subscribe({
          next: (coverage: Array<Coverage>) => {
            this.coverage.set(coverage);
            const updatedFetched = new Set([...fetched, ...geographyTypeIdsToLoad]);
            this.fetchedGeographyTypeIds.set(updatedFetched);
          },
          error: () => {
          }
        });
    }
  }

  private buildGeographiesTree(): void {
    const mapped = this.treeNodeService.mapGeographiesToTreeNodes(
        this.geographiesWithMissingParents(),
        this.userGeographies(),
        this.selectedGeographies()
    );
    this.nodes.set(mapped);
    this.selectedItems.set(this.selectedGeographies());
    this.refreshNodesMenuOptions();
    this.refreshFavouriteNodes();
    this.isDataLoading.set(false);
  }

  private refreshNodesMenuOptions(): void {
    this.treeNodeService.refreshGeographyNodesMenuOptions(
      [...this.nodes()],
      this.tree,
      [...this.userGeographies()],
      this.geographyService
    );
  }

  private refreshFavouriteNodes(): void {
    const currentNodes = this.nodes();
    const favourites = this.favouriteGeographies();
    if (!currentNodes?.length || !favourites?.length) {
        this.refreshCombinedSearchNodes();
        return;
    }
    const updated = this.treeNodeService.mapFavouritesToTreeNodes(
        favourites,
        currentNodes,
        this.favouriteNodes(),
        this.highlightedFavouriteCodes
    );
    this.favouriteNodes.set(updated);
    this.refreshCombinedSearchNodes();
  }

  private refreshCombinedSearchNodes(): void {
    const favouriteSearchNodes = this.favouriteNodes().map(fav => ({
        ...fav,
        children: [] as Array<IGeographyNode>
    })) as unknown as Array<IGeographyNode>;
    this.combinedSearchNodes.set([...this.nodes(), ...favouriteSearchNodes]);
  }

  private restoreHighlightedFavourites(): void {
    const selectedCodes = new Set(
        (this.dialogData.selectedGeographies ?? []).map(g => g.emiCode)
    );

    const currentNodes = this.nodes();
    const favourites = this.favouriteGeographies();

    favourites.forEach(favourite => {
        const memberNodes = favourite.geographyIds
            .map(id => this.findNodeByGeographyId(currentNodes, id))
            .filter((node): node is IGeographyNode => !!node)
            .filter(node => !node.isDisabled);

        if (
            memberNodes.length > 0 &&
            memberNodes.every(node => selectedCodes.has(node.code))
        ) {
            this.highlightedFavouriteCodes.add(favourite.id);
        }
    });
    this.refreshFavouriteNodes();
  }

  private findNodeByGeographyId(
      nodes: Array<IGeographyNode>,
      geographyId: string
  ): IGeographyNode | null {
      for (const node of nodes) {
          if (node.geographyId === geographyId) {
              return node;
          }
          if (node.children?.length) {
              const found = this.findNodeByGeographyId(node.children, geographyId);
              if (found) return found;
          }
      }
      return null;
  }
  
  private collectDescendantCodes(
      node: IGeographyNode,
      codes: Set<string>
  ): void {
      node.children?.forEach(child => {
          codes.add(child.code);
          if (child.children?.length) {
              this.collectDescendantCodes(child, codes);
          }
      });
  }
}